import { useState, useMemo, useCallback } from "react";
import { UrlApi } from "../../../services/apirest";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";

const BulkApprove = ({
  open,
  onClose,
  employees,
  year,
  sucursal,
  showToast,
}) => {
  const [selectedLegajos, setSelectedLegajos] = useState(
    employees.map((e) => e.empleadoId)
  );
  const [filterText, setFilterText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const allEmployeesLegajos = useMemo(
    () => employees.map((e) => e.empleadoId),
    [employees]
  );
  const allSelected = selectedLegajos.length === employees.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedLegajos((prev) =>
      prev.length === allEmployeesLegajos.length ? [] : allEmployeesLegajos
    );
  }, [allEmployeesLegajos]);

  const toggleEmployee = useCallback((legajo) => {
    setSelectedLegajos((prev) =>
      prev.includes(legajo)
        ? prev.filter((l) => l !== legajo)
        : [...prev, legajo]
    );
  }, []);

  const employeesFilteredAndSorted = useMemo(() => {
    const lowerCaseFilterText = filterText.toLowerCase().trim();

    const filtered = lowerCaseFilterText
      ? employees.filter((e) => {
          const nameMatch = e.nombre
            .toLowerCase()
            .includes(lowerCaseFilterText);
          const idMatch = String(e.empleadoId).includes(lowerCaseFilterText);

          return nameMatch || idMatch;
        })
      : employees;

    return filtered.sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
    );
  }, [employees, filterText]);

  const handleSubmit = async () => {
    if (selectedLegajos.length === 0) {
      showToast("Debes seleccionar al menos un empleado.", "warning");
      return;
    }

    setIsLoading(true);

    try {
      const url = `${UrlApi}/vacations/approve/${year}`;
      const response = await fetch(url, {
        method: "PUT",
        body: JSON.stringify({ legajos: selectedLegajos }),
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const pdfBlob = await response.blob();
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `constancias_aprobadas_${sucursal}_${year}.pdf`;

        if (contentDisposition) {
          const filenameMatch =
            contentDisposition.match(/filename="?([^"]*)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast("Aprobación y descarga del PDF completadas.", "success");
        onClose();
      } else {
        const errorText = await response.text();
        let errorMessage = "Error en la aprobación. Detalles en consola.";
        let consoleError = errorText;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          console.error(
            `[BulkApprove Error] Status: ${response.status}`,
            consoleError
          );
          showToast(errorMessage, "error");
        }
      }
    } catch (error) {
      console.error("[BulkApprove Fetch Error]", error);
      showToast("Error de red o procesamiento. Revisa la conexión.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchInput = () => (
    <TextField
      label="Buscar empleado..."
      placeholder="Escribe nombre o legajo..."
      value={filterText}
      onChange={(e) => setFilterText(e.target.value)}
      InputProps={{
        endAdornment: filterText && (
          <Button
            onClick={() => setFilterText("")}
            sx={{ mr: -1, minWidth: 0 }}
          >
            X
          </Button>
        ),
      }}
      fullWidth
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Seleccionar empleados para aprobación ({year})</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ py: 1 }}>
          {renderSearchInput()}

          <FormControlLabel
            control={
              <Checkbox checked={allSelected} onChange={toggleSelectAll} />
            }
            label={
              allSelected
                ? "Deseleccionar todos"
                : `Seleccionar todos (${employees.length})`
            }
            sx={{
              display: "block",
              my: 2,
              pb: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          />

          <Stack
            spacing={1}
            sx={{ maxHeight: "300px", overflowY: "auto", mb: 2 }}
          >
            {employeesFilteredAndSorted.length > 0 ? (
              employeesFilteredAndSorted.map((employee) => (
                <FormControlLabel
                  key={employee.empleadoId}
                  control={
                    <Checkbox
                      checked={selectedLegajos.includes(employee.empleadoId)}
                      onChange={() => toggleEmployee(employee.empleadoId)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {employee.nombre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Legajo: {employee.empleadoId}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "action.hover" },
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                />
              ))
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2, p: 1 }}>
                No se encontraron empleados con el filtro aplicado.
              </Typography>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" align="right">
            Seleccionados: {selectedLegajos.length} de {employees.length}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Save />}
          disabled={selectedLegajos.length === 0 || isLoading}
        >
          {isLoading ? "Aprobando..." : "Aprobar y descargar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkApprove;
